import { ChangeSummary, fieldCount } from './ChangeSummary';

import type { GetGenerationResponse } from '@platform/client';

/**
 * What the rewrite changed — GEN-56.
 *
 * The console reported the job's *status* and nothing about its *result*, so verifying an
 * instruction like "make the third option less obviously wrong" meant re-reading the whole
 * scene and remembering what it used to say.
 *
 * Old beside new, field by field, with the option to put it back. A rewrite that changed
 * nothing says so — that is a real outcome and it used to look identical to one that
 * changed everything. The layout is `ChangeSummary`, shared with the editor's applied
 * findings; the words and the verb are what differ between the two.
 */

type Row = GetGenerationResponse['rows'][number];

export function RewriteChanges({
  row,
  disabled,
  onDiscard,
}: {
  row: Row | undefined;
  disabled: boolean;
  onDiscard: () => void;
}) {
  const changes = row?.changes;

  if (changes === undefined) return null;

  return (
    <ChangeSummary
      changes={changes}
      unchanged="The rewrite came back with the same text. Nothing changed."
      lead={`The rewrite changed ${fieldCount(changes)}.`}
      // Shown when the turn carried one back, and its absence is not an error: the
      // planner is not required to explain itself — so the note is attributed to it and
      // not to the editor that ran the rewrite.
      {...(changes.rationale === undefined
        ? {}
        : { note: { author: 'Planner', text: changes.rationale } })}
      actions={[
        { label: 'Discard the rewrite', colour: 'error', onClick: onDiscard },
      ]}
      disabled={disabled}
    />
  );
}
